import { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, MessageCircle, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WA = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`;
const EMAIL = process.env.REACT_APP_CONTACT_EMAIL;

export default function Contact() {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', job_type: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  useEffect(() => {
    document.title = `${t('contact.title')} | Orange Onderhoudsbedrijf`;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setStatus('success');
      setForm({ name: '', email: '', phone: '', job_type: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <div className="bg-[#F8FAFC] pt-20">
      {/* Header */}
      <section data-testid="contact-header" className="bg-white border-b border-slate-100 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Orange Onderhoudsbedrijf</span>
          <h1 className="text-5xl md:text-6xl font-black text-[#0F172A] mt-3 mb-5">{t('contact.title')}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed">{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Content */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            {status === 'success' ? (
              <div data-testid="contact-success" className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center">
                <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                <h3 className="font-bold text-[#0F172A] text-xl mb-2">{t('contact.success')}</h3>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white border border-slate-100 rounded-2xl p-8 space-y-5 shadow-sm">
                {/* Row 1: Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#334155] mb-2">{t('contact.name')} *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder={t('contact.namePlaceholder')}
                      data-testid="contact-name"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#FF5F00] focus:bg-white transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#334155] mb-2">{t('contact.email')} *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder={t('contact.emailPlaceholder')}
                      data-testid="contact-email"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#FF5F00] focus:bg-white transition-all text-sm"
                    />
                  </div>
                </div>
                {/* Row 2: Phone + Job Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#334155] mb-2">{t('contact.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder={t('contact.phonePlaceholder')}
                      data-testid="contact-phone"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#FF5F00] focus:bg-white transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#334155] mb-2">{t('contact.jobType')} *</label>
                    <select
                      name="job_type"
                      required
                      value={form.job_type}
                      onChange={handleChange}
                      data-testid="contact-job-type"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[#0F172A] focus:outline-none focus:border-[#FF5F00] focus:bg-white transition-all text-sm"
                    >
                      <option value="">{t('contact.jobTypePlaceholder')}</option>
                      <option value="Handyman Solutions">{t('contact.jobTypeHandyman')}</option>
                      <option value="Gemeente Services">{t('contact.jobTypeMunicipality')}</option>
                    </select>
                  </div>
                </div>
                {/* Subject */}
                <div>
                  <label className="block text-sm font-semibold text-[#334155] mb-2">{t('contact.subject')} *</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    value={form.subject}
                    onChange={handleChange}
                    placeholder={t('contact.subjectPlaceholder')}
                    data-testid="contact-subject"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#FF5F00] focus:bg-white transition-all text-sm"
                  />
                </div>
                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-[#334155] mb-2">{t('contact.message')} *</label>
                  <textarea
                    name="message"
                    required
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder={t('contact.messagePlaceholder')}
                    data-testid="contact-message"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#FF5F00] focus:bg-white transition-all text-sm resize-none"
                  />
                </div>
                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl">
                    <AlertCircle size={16} />
                    <span className="text-sm">{t('contact.error')}</span>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  data-testid="contact-submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#FF5F00] text-white py-4 rounded-xl font-bold hover:bg-[#EA580C] transition-all disabled:opacity-60 disabled:cursor-not-allowed active:scale-98 text-sm"
                >
                  <Send size={16} />
                  {status === 'loading' ? t('contact.sending') : t('contact.send')}
                </button>
              </form>
            )}
          </div>

          {/* Info sidebar */}
          <div className="space-y-6">
            <div data-testid="contact-info" className="bg-white border border-slate-100 rounded-2xl p-7 shadow-sm">
              <h3 className="font-bold text-[#0F172A] text-lg mb-5">{t('contact.infoTitle')}</h3>
              <div className="space-y-4">
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-3 group"
                  data-testid="contact-email-link"
                >
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-[#FF5F00]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8] font-semibold uppercase tracking-wide">{t('contact.emailLabel')}</p>
                    <p className="text-[#334155] text-sm font-medium group-hover:text-[#FF5F00] transition-colors break-all">
                      {EMAIL}
                    </p>
                  </div>
                </a>
                <a
                  href={WA}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 group"
                  data-testid="contact-whatsapp-link"
                >
                  <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle size={18} className="text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8] font-semibold uppercase tracking-wide">{t('contact.whatsappLabel')}</p>
                    <p className="text-[#334155] text-sm font-medium group-hover:text-green-500 transition-colors">
                      WhatsApp
                    </p>
                  </div>
                </a>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#334155]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8] font-semibold uppercase tracking-wide">{t('contact.serviceAreaTitle')}</p>
                    <p className="text-[#334155] text-sm font-medium">{t('contact.serviceAreaText')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href={`${WA}?text=Hallo%2C%20ik%20heb%20een%20vraag%20over%20uw%20diensten.`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta"
              className="flex items-center justify-center gap-3 bg-[#25D366] text-white w-full py-4 rounded-2xl font-bold hover:bg-[#1EBE58] transition-all shadow-md"
            >
              <MessageCircle size={20} />
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
